import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';

const CATEGORIES = [
  { value: 'bug_report', label: 'Bug Report', description: 'Something is not working correctly' },
  { value: 'feature_request', label: 'Feature Request', description: 'Suggest a new feature or improvement' },
  { value: 'account_issue', label: 'Account Issue', description: 'Problems with login or account settings' },
  { value: 'observation_issue', label: 'Observation Issue', description: 'Questions about scheduling or observations' },
  { value: 'general_question', label: 'General Question', description: 'Other questions or inquiries' },
  { value: 'other', label: 'Other', description: 'Anything else' }
];

// Support email can be overridden via environment variable
const SUPPORT_EMAIL = process.env.OVERRIDE_SUPPORT_EMAIL;

const ContactSupport = ({ onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const handleCancel = () => {
    if (onClose) onClose();
    else navigate(-1);
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [ticketNumber, setTicketNumber] = useState(null);

  const [formData, setFormData] = useState({
    subject: '',
    category: '',
    description: ''
  });

  const [formErrors, setFormErrors] = useState({
    subject: '',
    category: '',
    description: ''
  });

  const validateForm = () => {
    const errors = {};

    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    } else if (formData.subject.trim().length < 5) {
      errors.subject = 'Subject must be at least 5 characters';
    } else if (formData.subject.trim().length > 200) {
      errors.subject = 'Subject cannot exceed 200 characters';
    }

    if (!formData.category) {
      errors.category = 'Please select a category';
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    } else if (formData.description.trim().length < 20) {
      errors.description = 'Description must be at least 20 characters';
    } else if (formData.description.trim().length > 5000) {
      errors.description = 'Description cannot exceed 5000 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.submitSupportRequest({
        subject: formData.subject.trim(),
        category: formData.category,
        description: formData.description.trim()
      });

      if (response.success) {
        setTicketNumber(response.data.ticket_number);
        setSuccess(response.data.message);
        // Reset form
        setFormData({
          subject: '',
          category: '',
          description: ''
        });
      } else {
        setError(response.error || 'Failed to submit support request');
      }
    } catch (err) {
      console.error('Failed to submit support request:', err);
      setError(err.response?.data?.error || 'Failed to submit support request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewRequest = () => {
    setSuccess(null);
    setTicketNumber(null);
    setError(null);
  };

  // Success state - show confirmation
  if (success && ticketNumber) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Request Submitted Successfully
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'monospace',
              bgcolor: 'action.hover',
              py: 1,
              px: 2,
              borderRadius: 1,
              display: 'inline-block',
              mb: 2
            }}
          >
            {ticketNumber}
          </Typography>
          <Typography color="text.secondary" paragraph>
            {success}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            A confirmation has been sent to <strong>{user?.email}</strong>
          </Typography>
          <Button
            variant="outlined"
            onClick={handleNewRequest}
            sx={{ mt: 2 }}
          >
            Submit Another Request
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Contact Support
      </Typography>
      <Typography color="text.secondary" paragraph>
        Have a question, issue, or suggestion? Fill out the form below and our team will respond within 24-48 hours.
        {SUPPORT_EMAIL && (
          <> You can also email us directly at <strong>{SUPPORT_EMAIL}</strong>.</>
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Category Select */}
            <FormControl fullWidth error={!!formErrors.category}>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                name="category"
                value={formData.category}
                onChange={handleChange}
                label="Category"
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box>
                      <Typography>{cat.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {cat.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {formErrors.category && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {formErrors.category}
                </Typography>
              )}
            </FormControl>

            {/* Subject */}
            <TextField
              fullWidth
              label="Subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              error={!!formErrors.subject}
              helperText={formErrors.subject || `${formData.subject.length}/200 characters`}
              inputProps={{ maxLength: 200 }}
              placeholder="Brief summary of your request"
            />

            {/* Description */}
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              error={!!formErrors.description}
              helperText={formErrors.description || `${formData.description.length}/5000 characters (minimum 20)`}
              multiline
              rows={6}
              inputProps={{ maxLength: 5000 }}
              placeholder="Please provide as much detail as possible. Include any error messages, steps to reproduce issues, or context that would help us assist you."
            />

            {/* Contact Info Display */}
            <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Your request will be submitted from: <strong>{user?.email}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                We will respond to this email address.
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                disabled={loading}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ContactSupport;
